import { createClient, SupabaseClient } from '@supabase/supabase-js';

import { sanitizeWidgetMessage, type ChatMessage } from './api-client';

export interface WidgetRealtimeConfig {
  supabase_url: string;
  supabase_anon_key: string;
}

export interface WidgetRealtimeCallbacks {
  onNewMessage?: (message: ChatMessage) => void;
  onAssignmentChanged?: (type: string, assignedTo: string | null) => void;
  onError?: (error: string) => void;
}

/** Extended message type for session-level notifications that includes conversation_id */
export interface SessionMessage extends ChatMessage {
  conversation_id?: string;
}

export interface SessionRealtimeCallbacks {
  onNewMessage?: (message: SessionMessage, conversationId: string) => void;
  onError?: (error: string) => void;
}

let supabaseInstance: SupabaseClient | null = null;

export function initializeWidgetRealtime(url: string, anonKey: string) {
  if (!supabaseInstance) {
    // anonKey may be the legacy JWT anon key or sb_publishable_...
    supabaseInstance = createClient(url, anonKey);
  }
  return supabaseInstance;
}

/**
 * Subscribe to realtime events for a specific conversation.
 * Used when the user has a conversation open.
 * (Not a React hook — safe to call from class components.)
 */
export function subscribeConversationRealtime(
  conversationId: string | null,
  realtimeConfig: WidgetRealtimeConfig | null,
  callbacks: WidgetRealtimeCallbacks,
): (() => void) | null {
  if (!conversationId || !realtimeConfig) {
    return null;
  }

  const supabase = initializeWidgetRealtime(
    realtimeConfig.supabase_url,
    realtimeConfig.supabase_anon_key,
  );

  const channel = supabase
    .channel(`conversation:${conversationId}`)
    .on('broadcast', { event: 'new_message' }, (payload) => {
      const message = sanitizeWidgetMessage(payload.payload as ChatMessage);
      callbacks.onNewMessage?.(message);
    })
    .on('broadcast', { event: 'assignment_changed' }, (payload) => {
      const data = payload.payload as {
        assignment_type?: string;
        assigned_to?: string | null;
      };
      if (data.assignment_type) {
        callbacks.onAssignmentChanged?.(
          data.assignment_type,
          data.assigned_to ?? null,
        );
      }
    })
    .subscribe((status) => {
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        callbacks.onError?.(`Realtime subscription ${status.toLowerCase()}`);
      }
    });

  return () => {
    channel.unsubscribe();
  };
}

/**
 * Subscribe to realtime events for a session (all conversations).
 * Used for global notifications when the user is not viewing a specific conversation.
 * (Not a React hook — safe to call from class components.)
 */
export function subscribeSessionRealtime(
  sessionId: string | null,
  realtimeConfig: WidgetRealtimeConfig | null,
  callbacks: SessionRealtimeCallbacks,
): (() => void) | null {
  if (!sessionId || !realtimeConfig) {
    return null;
  }

  const supabase = initializeWidgetRealtime(
    realtimeConfig.supabase_url,
    realtimeConfig.supabase_anon_key,
  );

  const channel = supabase
    .channel(`widget_session:${sessionId}`)
    .on('broadcast', { event: 'new_message' }, (payload) => {
      const message = sanitizeWidgetMessage(payload.payload as SessionMessage);
      const conversationId = message.conversation_id;
      if (conversationId) {
        callbacks.onNewMessage?.(message, conversationId);
      }
    })
    .subscribe((status) => {
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        callbacks.onError?.(`Session realtime subscription ${status.toLowerCase()}`);
      }
    });

  return () => {
    channel.unsubscribe();
  };
}
